import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/Modal';
import EventStatusBadge from '../../components/EventStatusBadge';
import { Button, EmptyState, LoadingSpinner, OverflowMenu } from '../../components/ui';
import type { OverflowMenuItem } from '../../components/ui';
import {
  buildMemberLedger,
  getDisplayBalanceClass,
  toDisplayBalance,
  type LedgerCharge,
  type LedgerPayment,
  type MemberBalance,
  type PaymentItem,
} from './paymentLedger';
import '../../components/MemberPayments.css';
import './PaymentOverviewPage.css';
import './MemberLedgerModal.css';

interface MemberLedgerModalProps {
  member: MemberBalance | null;
  payments: PaymentItem[] | undefined;
  paymentsLoading: boolean;
  /** The payments fetch failed — say so instead of showing an empty ledger. */
  paymentsError: boolean;
  /** The page stopped paging before the end — the ledger below is partial. */
  paymentsTruncated: boolean;
  onRetryPayments: () => void;
  canWrite: boolean;
  canDelete: boolean;
  onClose: () => void;
  onMarkPaid: (charge: LedgerCharge) => void;
  onCancelCharge: (intentId: string) => void;
  onRefund: (payment: LedgerPayment) => void;
  onDeletePayment: (payment: LedgerPayment) => void;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(isoString: string): string {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString();
}

const MemberLedgerModal: React.FC<MemberLedgerModalProps> = ({
  member,
  payments,
  paymentsLoading,
  paymentsError,
  paymentsTruncated,
  onRetryPayments,
  canWrite,
  canDelete,
  onClose,
  onMarkPaid,
  onCancelCharge,
  onRefund,
  onDeletePayment,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'charges' | 'payments'>('charges');

  const ledger = useMemo(
    () => (member ? buildMemberLedger(member, payments ?? []) : null),
    [member, payments],
  );

  if (!member || !ledger) return null;

  const chargeActions = (charge: LedgerCharge): OverflowMenuItem[] => {
    const items: OverflowMenuItem[] = [];
    if (canWrite && charge.balance > 0 && charge.sourceId) {
      items.push({
        key: 'mark-paid',
        label: t('paymentOverview.markAsPaid'),
        onSelect: () => onMarkPaid(charge),
      });
    }
    // cancellableIntentId is the API's own "pending, nothing paid" signal; the
    // paid check also covers money recorded outside the intent.
    if (canDelete && charge.cancellableIntentId && charge.paid === 0 && charge.balance > 0) {
      items.push({
        key: 'cancel-charge',
        label: t('paymentOverview.cancelCharge'),
        variant: 'danger',
        onSelect: () => onCancelCharge(charge.cancellableIntentId!),
      });
    }
    return items;
  };

  const paymentActions = (payment: LedgerPayment): OverflowMenuItem[] => {
    const items: OverflowMenuItem[] = [];
    if (canWrite && payment.amount > 0) {
      items.push({
        key: 'refund',
        label: t('payments.refund'),
        onSelect: () => onRefund(payment),
      });
    }
    if (canDelete) {
      items.push({
        key: 'delete',
        label: t('payments.deletePayment'),
        variant: 'danger',
        onSelect: () => onDeletePayment(payment),
      });
    }
    return items;
  };

  const paymentRow = (payment: LedgerPayment) => (
    <tr key={`payment-${payment.id}`} className="member-ledger__row member-ledger__row--payment">
      <td className="member-ledger__cell-name member-ledger__cell-name--nested">
        <div className="member-ledger__stack">
          <span className="member-ledger__date">{formatDate(payment.date)}</span>
          {payment.label && <span className="member-ledger__payment-label">{payment.label}</span>}
        </div>
      </td>
      <td></td>
      <td className="member-ledger__num">{formatCurrency(payment.amount)}</td>
      <td></td>
      <td className="member-ledger__cell-actions">
        <OverflowMenu
          label={t('paymentOverview.paymentActions', { name: payment.label || formatDate(payment.date) })}
          items={paymentActions(payment)}
        />
      </td>
    </tr>
  );

  const chargeRows = ledger.charges.map((charge) => {
    const display = toDisplayBalance(charge.balance);
    const period = charge.dateTo
      ? `${formatDate(charge.dateFrom)} – ${formatDate(charge.dateTo)}`
      : formatDate(charge.dateFrom);
    return (
      <React.Fragment key={charge.key}>
        <tr className="member-ledger__row member-ledger__row--charge">
          <td className="member-ledger__cell-name">
            <div className="member-ledger__stack">
              <span className="member-ledger__kind">
                {charge.kind === 'event' ? t('paymentOverview.event') : t('paymentOverview.membership')}
              </span>
              <span className="member-ledger__name">{charge.name}</span>
              <span className="member-ledger__date">{period}</span>
              {/* Explains an `owed` that is the amount received, not the price. */}
              {charge.cancelled && <EventStatusBadge status="cancelled" compact />}
              {charge.attending === false && charge.balance > 0 && (
                <span className="member-ledger__tag">{t('paymentOverview.notAttendingTag')}</span>
              )}
            </div>
          </td>
          <td className="member-ledger__num">{formatCurrency(charge.owed)}</td>
          <td className="member-ledger__num">{formatCurrency(charge.paid)}</td>
          <td className={`member-ledger__num member-ledger__num--strong ${getDisplayBalanceClass(display)}`}>
            {formatCurrency(display)}
          </td>
          <td className="member-ledger__cell-actions">
            <OverflowMenu
              label={t('paymentOverview.chargeActions', { name: charge.name })}
              items={chargeActions(charge)}
            />
          </td>
        </tr>
        {charge.payments.map(paymentRow)}
      </React.Fragment>
    );
  });

  const splitRows = ledger.splits.map((split) => (
    <React.Fragment key={split.key}>
      <tr className="member-ledger__row member-ledger__row--charge">
        <td className="member-ledger__cell-name" colSpan={4}>
          <div className="member-ledger__stack">
            <span className="member-ledger__name">{split.sourceName}</span>
            <span className="member-ledger__tag member-ledger__tag--info">
              {t('paymentOverview.splitAcross', { names: split.chargeNames.join(', ') })}
            </span>
          </div>
        </td>
        <td></td>
      </tr>
      {split.payments.map(paymentRow)}
    </React.Fragment>
  ));

  const section = (key: string, label: string, rows: LedgerPayment[]) => (rows.length > 0 ? (
    <React.Fragment key={key}>
      <tr className="member-ledger__row member-ledger__row--section">
        <td colSpan={5}>{label}</td>
      </tr>
      {rows.map(paymentRow)}
    </React.Fragment>
  ) : null);

  const noMatchingChargeRows = section(
    'no-matching-charge',
    t('paymentOverview.noMatchingCharge'),
    ledger.noMatchingCharge,
  );
  const unallocatedRows = section('unallocated', t('paymentOverview.unallocated'), ledger.unallocated);

  const isEmpty = ledger.charges.length === 0
    && ledger.splits.length === 0
    && ledger.noMatchingCharge.length === 0
    && ledger.unallocated.length === 0;

  const memberDisplay = toDisplayBalance(member.balance);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xlarge"
      title={t('paymentOverview.ledgerTitle', { member: member.memberName })}
    >
      <div className="member-ledger">
        <div className="member-ledger__summary">
          <div className="member-ledger__summary-item">
            <span className="member-ledger__summary-label">{t('paymentOverview.totalOwed')}</span>
            <span className="member-ledger__summary-value">{formatCurrency(member.totalOwed)}</span>
          </div>
          <div className="member-ledger__summary-item">
            <span className="member-ledger__summary-label">{t('paymentOverview.totalPaid')}</span>
            <span className="member-ledger__summary-value">{formatCurrency(member.totalPaid)}</span>
          </div>
          <div className="member-ledger__summary-item">
            <span className="member-ledger__summary-label">{t('paymentOverview.balance')}</span>
            <span className={`member-ledger__summary-value ${getDisplayBalanceClass(memberDisplay)}`}>
              {formatCurrency(memberDisplay)}
            </span>
          </div>
        </div>

        {paymentsError && (
          <div className="member-ledger__error" role="alert">
            <span>{t('paymentOverview.paymentsLoadError')}</span>
            <Button variant="secondary" size="sm" onClick={onRetryPayments}>
              {t('common.retry')}
            </Button>
          </div>
        )}

        {paymentsTruncated && !paymentsError && (
          <div className="member-ledger__error member-ledger__error--notice" role="status">
            <span>{t('paymentOverview.paymentsTruncated')}</span>
          </div>
        )}

        <div className="payments-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            id="member-ledger-tab-charges"
            aria-selected={activeTab === 'charges'}
            aria-controls="member-ledger-panel"
            className={`payments-tab ${activeTab === 'charges' ? 'active' : ''}`}
            onClick={() => setActiveTab('charges')}
          >
            {t('paymentOverview.chargesTab')}
            <span className="tab-badge">{ledger.charges.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            id="member-ledger-tab-payments"
            aria-selected={activeTab === 'payments'}
            aria-controls="member-ledger-panel"
            className={`payments-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            {t('paymentOverview.paymentsTab')}
            <span className="tab-badge">{ledger.allPayments.length}</span>
          </button>
        </div>

        <div
          id="member-ledger-panel"
          role="tabpanel"
          aria-labelledby={`member-ledger-tab-${activeTab}`}
        >

        {paymentsLoading && payments === undefined ? (
          <div className="member-ledger__loading">
            <LoadingSpinner text={t('paymentOverview.loading')} />
          </div>
        ) : activeTab === 'charges' ? (
          isEmpty ? (
            <EmptyState title={t('paymentOverview.noCharges')} size="sm" />
          ) : (
            <div className="member-ledger__table-wrap">
              <table className="member-ledger__table">
                <thead>
                  <tr>
                    <th>{t('paymentOverview.chargeName')}</th>
                    <th className="member-ledger__num">{t('paymentOverview.totalOwed')}</th>
                    <th className="member-ledger__num">{t('paymentOverview.totalPaid')}</th>
                    <th className="member-ledger__num">{t('paymentOverview.balance')}</th>
                    <th className="member-ledger__cell-actions">{t('paymentOverview.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {chargeRows}
                  {splitRows}
                  {noMatchingChargeRows}
                  {unallocatedRows}
                </tbody>
              </table>
            </div>
          )
        ) : ledger.allPayments.length === 0 ? (
          <EmptyState
            title={paymentsError
              ? t('paymentOverview.paymentsLoadError')
              : t('paymentOverview.noPaymentsForMember')}
            size="sm"
            {...(paymentsError ? { action: { label: t('common.retry'), onClick: onRetryPayments } } : {})}
          />
        ) : (
          <div className="member-ledger__table-wrap">
            <table className="member-ledger__table">
              <thead>
                <tr>
                  <th>{t('paymentOverview.paymentDate')}</th>
                  <th>{t('paymentOverview.chargeName')}</th>
                  <th className="member-ledger__num">{t('paymentOverview.amount')}</th>
                  <th className="member-ledger__cell-actions">{t('paymentOverview.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {ledger.allPayments.map((payment) => (
                  <tr key={`flat-${payment.id}`} className="member-ledger__row">
                    <td className="member-ledger__date">{formatDate(payment.date)}</td>
                    <td className="member-ledger__cell-name">
                      <div className="member-ledger__stack">
                        <span className="member-ledger__name">
                          {payment.label || payment.source.description || '—'}
                        </span>
                      </div>
                    </td>
                    <td className={`member-ledger__num ${payment.amount < 0 ? 'payment-overview__balance--overpaid' : ''}`}>
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="member-ledger__cell-actions">
                      <OverflowMenu
                        label={t('paymentOverview.paymentActions', { name: payment.label || formatDate(payment.date) })}
                        items={paymentActions(payment)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </Modal>
  );
};

export default MemberLedgerModal;
